import type {
  AccuracyReport, Alert, AlertKind, AlertStatus, AppUser, AssistantReply, Benchmarks, ChannelForecast, Closure,
  ClosureInput, Consents, ConsentsUpdate, Forecast, ForecastTarget, GroupForecast, HourlyForecast, InfluencerReport,
  LocalEvent, LocalEventInput, LocationDetail, LocationSettingsPatch, MarketingActivity, MarketingInput, Me, MenuReport,
  ProductForecast, SettingsPatchResult, TenantSettings, TenantSettingsPatch, TradingDayPreview, UploadKind,
  UploadStatus, UploadTicket, UserInput, ConnectionsResponse, InstagramStatus, CalendarMonth, DayDetail, SquareConnection, SquareLocation, Place, OnboardingInput, OnboardingResult, PrepComponent, PrepComponentInput, PrepPlan, PrepSetup, RecipeLine, Campaign, CampaignInput, CampaignStatus, CampaignKind, QuietDays,
} from './types';

/**
 * The one interface every screen talks to. `HttpApiClient` calls the real REST API;
 * `MockApiClient` serves fixtures so the whole app runs without a backend.
 */
export interface ApiClient {
  getMe(): Promise<Me>;

  getLocation(locationId: string): Promise<LocationDetail>;
  patchLocationSettings(locationId: string, patch: LocationSettingsPatch): Promise<SettingsPatchResult>;
  previewTradingDay(locationId: string, tradingDayStart: string): Promise<TradingDayPreview>;
  getTenantSettings(): Promise<TenantSettings>;
  patchTenantSettings(patch: TenantSettingsPatch): Promise<TenantSettings>;

  getForecast(locationId: string, target: ForecastTarget, days: number): Promise<Forecast>;
  /** A calendar month coloured hot to cold; month = YYYY-MM, default the month after the forecast origin. */
  getDailyMonth(locationId: string, month?: string): Promise<CalendarMonth>;
  getDayDetail(locationId: string, day: string): Promise<DayDetail>;
  getHourlyForecast(locationId: string, days: number): Promise<HourlyForecast>;
  getChannelForecast(locationId: string, days: number): Promise<ChannelForecast>;
  getProductForecast(locationId: string, days: number): Promise<ProductForecast>;
  getGroupForecast(days: number): Promise<GroupForecast>;

  getAlerts(locationId: string, status?: AlertStatus, kind?: AlertKind): Promise<Alert[]>;
  updateAlert(alertId: string, status: Exclude<AlertStatus, 'open'>): Promise<Alert>;

  getAccuracy(locationId: string, window: number): Promise<AccuracyReport>;
  getMenu(locationId: string): Promise<MenuReport>;

  listEvents(locationId: string): Promise<LocalEvent[]>;
  createEvent(locationId: string, input: LocalEventInput): Promise<LocalEvent>;
  deleteEvent(eventId: string): Promise<void>;
  listMarketing(locationId: string): Promise<MarketingActivity[]>;
  createMarketing(locationId: string, input: MarketingInput): Promise<MarketingActivity>;
  deleteMarketing(activityId: string): Promise<void>;
  listClosures(locationId: string): Promise<Closure[]>;
  createClosure(locationId: string, input: ClosureInput): Promise<Closure>;
  deleteClosure(closureId: string): Promise<void>;

  getInfluencers(locationId: string): Promise<InfluencerReport>;

  createUpload(locationId: string, kind: UploadKind, filename: string): Promise<UploadTicket>;
  /** PUT the file to the presigned URL, reporting progress 0–1. */
  putFile(ticket: UploadTicket, file: File, onProgress: (fraction: number) => void): Promise<void>;
  getUpload(uploadId: string): Promise<UploadStatus>;

  getConsents(): Promise<Consents>;
  putConsents(update: ConsentsUpdate): Promise<Consents>;
  getBenchmarks(locationId: string): Promise<Benchmarks>;

  chat(locationId: string, message: string): Promise<AssistantReply>;

  /** Suburb suggestions for onboarding (signed in, no business yet). */
  findPlaces(query: string): Promise<Place[]>;
  /** Create the signed-up user's business, first site and free trial. */
  completeOnboarding(input: OnboardingInput): Promise<OnboardingResult>;

  getPrepSetup(locationId: string): Promise<PrepSetup>;
  createPrepComponent(locationId: string, input: PrepComponentInput): Promise<PrepComponent>;
  updatePrepComponent(componentId: string, patch: Partial<PrepComponentInput>): Promise<PrepComponent>;
  deletePrepComponent(componentId: string): Promise<void>;
  /** Replace what one menu item uses; an empty list clears it. */
  setRecipe(locationId: string, itemName: string, lines: RecipeLine[]): Promise<void>;
  getPrepPlan(locationId: string, day?: string): Promise<PrepPlan>;

  getQuietDays(locationId: string): Promise<QuietDays>;
  listCampaigns(locationId: string): Promise<Campaign[]>;
  createCampaign(locationId: string, input: CampaignInput): Promise<Campaign>;
  updateCampaign(campaignId: string, patch: { status?: CampaignStatus; details?: string; cost?: number | null }): Promise<Campaign>;
  deleteCampaign(campaignId: string): Promise<void>;
  draftPost(locationId: string, tradingDay: string, kind: CampaignKind, offer: string): Promise<string>;

  getConnections(locationId: string): Promise<ConnectionsResponse>;
  /** Returns Square's authorise URL; the browser navigates there. */
  startSquareConnect(locationId: string): Promise<{ url: string }>;
  getSquareLocations(connectionId: string): Promise<SquareLocation[]>;
  setSquareLocation(connectionId: string, squareLocationId: string): Promise<SquareConnection>;
  syncConnection(connectionId: string): Promise<{ status: string; orders?: number; posts?: number; error?: string | null }>;
  deleteConnection(connectionId: string): Promise<void>;
  getInstagram(locationId: string): Promise<InstagramStatus>;
  /** Returns Instagram's authorise URL; the browser navigates there. */
  startInstagramConnect(locationId: string): Promise<{ url: string }>;

  listUsers(): Promise<AppUser[]>;
  createUser(input: UserInput): Promise<AppUser>;
  updateUser(userId: string, patch: Partial<UserInput>): Promise<AppUser>;
  deleteUser(userId: string): Promise<void>;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}
